
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';

interface CategoryData {
  category_name: string;
  profile_content: any;
  processing_status: 'completed' | 'failed';
  error_message?: string;
}

interface AthleteIntelligenceScoreProps {
  completedCategories: CategoryData[];
  onScrollToAnalysis: () => void;
  onCategoryClick: (categoryName: string) => void;
  className?: string;
}

const categoryDisplayNames: Record<string, string> = {
  family: 'Family & Relationships',
  financial: 'Financial',
  career: 'Career',
  personal_life: 'Personal Life',
  agent: 'Agent',
  personality: 'Personality',
  early_career_and_education: 'Early Career & Education',
  adverse_media: 'Adverse Media',
  legal_and_criminal_history: 'Legal & Criminal History',
  sponsorships: 'Sponsorships',
  'on-field_comments': 'On-Field Comments'
};

const allCategories = [
  'family',
  'financial', 
  'career',
  'personal_life',
  'agent',
  'personality',
  'early_career_and_education',
  'adverse_media',
  'legal_and_criminal_history',
  'sponsorships',
  'on-field_comments'
];

// Enhanced color system with gradients
const getGradientColors = (score: number) => {
  if (score === 10) return { bg: '#e7c887', text: '#22333B' }; // Perfect score - gold
  if (score >= 9) return { bg: 'linear-gradient(135deg, #22c55e 0%, #e7c887 100%)', text: '#22333B' }; // Excellent
  if (score >= 8) return { bg: 'linear-gradient(135deg, #22c55e 0%, #65d373 100%)', text: '#22333B' }; // Very good
  if (score >= 7) return { bg: 'linear-gradient(135deg, #eab308 0%, #22c55e 100%)', text: '#22333B' }; // Good
  if (score >= 6) return { bg: 'linear-gradient(135deg, #eab308 0%, #fbbf24 100%)', text: '#22333B' }; // Average+
  if (score >= 5) return { bg: 'linear-gradient(135deg, #f97316 0%, #eab308 100%)', text: '#22333B' }; // Average
  if (score >= 4) return { bg: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', text: '#22333B' }; // Below average
  if (score >= 3) return { bg: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)', text: '#EAE0D5' }; // Poor
  if (score >= 2) return { bg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', text: '#EAE0D5' }; // Very poor
  return { bg: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)', text: '#EAE0D5' }; // Critical
};

const getAISGradientColors = (score: number) => {
  if (score >= 9) return ['#e7c887', '#22c55e'];
  if (score >= 8) return ['#22c55e', '#65d373'];
  if (score >= 7) return ['#eab308', '#22c55e'];
  if (score >= 6) return ['#eab308', '#fbbf24'];
  if (score >= 5) return ['#f97316', '#eab308'];
  if (score >= 4) return ['#f97316', '#fb923c'];
  if (score >= 3) return ['#ef4444', '#f97316'];
  return ['#dc2626', '#ef4444'];
};

const formatCategoryName = (name: string): string => {
  return categoryDisplayNames[name] || name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Create a mapping function to match frontend category names with backend names
const findMatchingCategory = (frontendCategoryName: string, completedCategories: CategoryData[]): CategoryData | undefined => {
  // First try exact match
  let match = completedCategories.find(c => c.category_name === frontendCategoryName);
  if (match) return match;
  
  // Try case-insensitive match
  match = completedCategories.find(c => c.category_name.toLowerCase() === frontendCategoryName.toLowerCase());
  if (match) return match;
  
  // Try fuzzy matching for known mappings
  const mappings: Record<string, string[]> = {
    'family': ['family_and_relationships', 'family'],
    'financial': ['financial'],
    'career': ['career'],
    'personal_life': ['personal_life'],
    'agent': ['agent'],
    'personality': ['personality'],
    'early_career_and_education': ['early_career_and_youth', 'early_career_and_education'],
    'adverse_media': ['adverse_media'],
    'legal_and_criminal_history': ['legal_criminal_or_compliance_issues', 'legal_and_criminal_history'],
    'sponsorships': ['sponsorships_and_commercial', 'sponsorships'],
    'on-field_comments': ['on_field_comments', 'on-field_comments']
  };
  
  const possibleNames = mappings[frontendCategoryName] || [];
  
  for (const possibleName of possibleNames) {
    match = completedCategories.find(c => 
      c.category_name.toLowerCase() === possibleName.toLowerCase() ||
      c.category_name.toLowerCase().replace(/[_-]/g, '') === possibleName.toLowerCase().replace(/[_-]/g, '')
    );
    if (match) return match;
  }
  
  // Last resort: partial matching
  const normalizedFrontend = frontendCategoryName.toLowerCase().replace(/[_-]/g, '');
  match = completedCategories.find(c => {
    const normalizedBackend = c.category_name.toLowerCase().replace(/[_-]/g, '');
    return normalizedBackend.includes(normalizedFrontend.split('_')[0]) || 
           normalizedFrontend.includes(normalizedBackend.split('_')[0]);
  });
  
  return match;
};

// Compact AIS Dial Component
const AISDial: React.FC<{ score: number; onClick: () => void }> = ({ score, onClick }) => {
  const radius = 45;
  const strokeWidth = 6;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (score / 10) * circumference;
  const gradientColors = getAISGradientColors(score);
  
  return (
    <motion.div 
      className="relative cursor-pointer group"
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <svg width="100" height="100" className="transform -rotate-90">
        <defs>
          <linearGradient id="aisGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradientColors[0]} />
            <stop offset="100%" stopColor={gradientColors[1]} />
          </linearGradient>
        </defs>
        
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke="#22333B"
          strokeWidth={strokeWidth}
          fill="transparent"
          opacity="0.3"
        />
        
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke="url(#aisGradient)"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-bold text-[#C6AC8E] group-hover:text-[#e7c887] transition-colors">
          {Math.round(score * 100)}
        </div>
        <div className="text-xs text-[#5E503F] group-hover:text-[#C6AC8E] transition-colors">
          AIS
        </div>
      </div>
    </motion.div>
  );
};

// Category Score Component with Tooltip
const CategoryScoreCard: React.FC<{ 
  category: any; 
  index: number; 
  onCategoryClick: (categoryName: string) => void;
  onScrollToAnalysis: () => void;
}> = ({ category, index, onCategoryClick, onScrollToAnalysis }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  
  const handleMouseEnter = () => {
    const timer = setTimeout(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        setTooltipPosition({
          x: rect.left + scrollLeft + rect.width / 2,
          y: rect.bottom + scrollTop + 8
        });
      }
      setShowTooltip(true);
    }, 1000); // Show after 1 second
    setTooltipTimer(timer);
  };
  
  const handleMouseLeave = () => {
    if (tooltipTimer) {
      clearTimeout(tooltipTimer);
      setTooltipTimer(null);
    }
    setShowTooltip(false);
  };
  
  useEffect(() => {
    return () => {
      if (tooltipTimer) {
        clearTimeout(tooltipTimer);
      }
    };
  }, [tooltipTimer]);

  const colors = getGradientColors(category.score);
  
  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.button
        ref={buttonRef}
        onClick={() => {
          onScrollToAnalysis();
          onCategoryClick(category.name);
        }}
        className={cn(
          "relative overflow-hidden p-2 rounded-lg border transition-all duration-200 text-center group flex-shrink-0",
          "min-w-[100px] max-w-[120px]",
          category.isCompleted
            ? "border-[#22333B] hover:border-[#C6AC8E] hover:shadow-lg"
            : "border-[#5E503F] cursor-not-allowed opacity-50"
        )}
        disabled={!category.isCompleted}
        whileHover={category.isCompleted ? { scale: 1.05 } : {}}
        whileTap={category.isCompleted ? { scale: 0.95 } : {}}
      >
        <div className="flex flex-col items-center space-y-2">
          {/* Large prominent score */}
          <div 
            className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold",
              "shadow-lg transition-all duration-200"
            )}
            style={{
              background: category.isCompleted ? colors.bg : '#5E503F',
              color: category.isCompleted ? colors.text : '#EAE0D5'
            }}
          >
            {category.isCompleted ? category.score.toFixed(1) : '?'}
          </div>
          
          {/* Compact category name */}
          <div className="space-y-1">
            <h4 className={cn(
              "text-xs font-medium leading-tight",
              category.isCompleted ? "text-[#EAE0D5] group-hover:text-[#C6AC8E]" : "text-[#5E503F]"
            )}>
              {category.displayName.split(' ').slice(0, 2).join(' ')}
            </h4>
          </div>
        </div>
      </motion.button>
      
      {/* Tooltip - Positioned near the hovered element */}
      <AnimatePresence>
        {showTooltip && category.isCompleted && category.explanation && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed z-[9999] bg-black/90 backdrop-blur-sm text-[#EAE0D5] text-xs p-3 rounded-lg max-w-xs border border-[#22333B] shadow-xl pointer-events-none"
            style={{ 
              top: tooltipPosition.y,
              left: tooltipPosition.x,
              transform: 'translateX(-50%)'
            }}
          >
            <div className="font-medium mb-1 text-[#C6AC8E]">Score Explanation</div>
            <div className="leading-relaxed">{category.explanation}</div>
            {/* Arrow pointing up */}
            <div 
              className="absolute w-2 h-2 bg-black/90 border-l border-t border-[#22333B] rotate-45"
              style={{
                top: '-4px',
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const AthleteIntelligenceScore: React.FC<AthleteIntelligenceScoreProps> = ({
  completedCategories,
  onScrollToAnalysis,
  onCategoryClick,
  className
}) => {
  // Calculate scores for each category
  const categoryScores = allCategories.map(categoryName => {
    const completedCategory = findMatchingCategory(categoryName, completedCategories);
    let rating = 0;
    let explanation = '';
    
    if (completedCategory?.profile_content) {
      try {
        // Check if profile_content is already an object or needs parsing
        const profileContent = typeof completedCategory.profile_content === 'string' 
          ? JSON.parse(completedCategory.profile_content)
          : completedCategory.profile_content;
        rating = profileContent?.rating?.score || 0;
        explanation = profileContent?.rating?.explanation || '';
      } catch (e) {
        console.error('Error parsing profile content:', e, 'Raw content:', completedCategory.profile_content);
      }
    }
    
    return {
      name: categoryName,
      displayName: formatCategoryName(categoryName),
      score: rating,
      explanation,
      isCompleted: !!completedCategory
    };
  });
  
  // Calculate AIS (average of completed categories)
  const completedScores = categoryScores.filter(c => c.isCompleted && c.score > 0);
  const aisScore = completedScores.length > 0 
    ? completedScores.reduce((sum, c) => sum + c.score, 0) / completedScores.length 
    : 0;
  
  // Don't render if no categories are completed
  if (completedScores.length === 0) {
    return null;
  }
  
  return (
    <motion.div 
      className={cn("bg-[#1A1A1A] rounded-lg border border-[#22333B] p-3 relative", className)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Single Row Layout - Scrollable */}
      <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-[#22333B] scrollbar-track-transparent">
        {categoryScores.map((category, index) => (
          <CategoryScoreCard
            key={category.name}
            category={category}
            index={index}
            onCategoryClick={onCategoryClick}
            onScrollToAnalysis={onScrollToAnalysis}
          />
        ))}
      </div>
      
      {/* Click hint */}
      <div className="mt-2 text-center">
        <p className="text-xs text-[#5E503F]">
          Click any score to view detailed analysis • Hover for explanations
        </p>
      </div>
    </motion.div>
  );
};
